import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, linkedSignal, signal } from '@angular/core';
import {
  LanguageEnum,
  ILlmConfigDto,
  IUseOwnLlmDto,
  ILlmModelDto,
} from '@shared';
import { forkJoin, Observable, tap } from 'rxjs';

import { AuthApiService } from '../../../core/auth/auth-api.service';
import { ISession } from '../models/session.model';
import { IUserProfile } from '../models/user-profile.model';

import { ProfileApiService } from './profile-api.service';

@Injectable({ providedIn: 'root' })
export class ProfileService {
  readonly #profileApiService = inject(ProfileApiService);
  readonly #authApiService = inject(AuthApiService);

  readonly user = signal<IUserProfile | null>(null);
  readonly sessions = signal<ISession[]>([]);
  readonly geminiApiKey = signal<null | string>(null);
  readonly llmUrl = signal<null | string>(null);
  readonly llmModelName = signal<null | string>(null);
  readonly llmAuthToken = signal<null | string>(null);
  readonly useOwnLlm = signal(false);
  readonly llmModels = signal<ILlmModelDto[]>([]);
  readonly isLoadingLlmModels = signal(false);
  readonly llmModelsError = signal<null | string>(null);

  readonly language = linkedSignal(
    () => this.user()?.language || LanguageEnum.EN,
  );

  initializeLlmState(): void {
    forkJoin({
      config: this.#authApiService.getLlmConfig(),
      useOwn: this.#authApiService.getUseOwnLlm(),
    }).subscribe(({ config, useOwn }) => {
      this.llmAuthToken.set(config.llmAuthToken);
      this.llmUrl.set(config.llmUrl);
      this.llmModelName.set(config.llmModelName);
      this.useOwnLlm.set(useOwn.useOwnLlm);

      if (this.useOwnLlm() && this.llmUrl()) {
        this.loadLlmModels();
      }
    });
  }

  loadUserProfile(): void {
    this.#profileApiService.getUserProfile().subscribe((profile) => {
      this.user.set(profile);
    });
  }

  loadSessions(): void {
    this.#profileApiService.getSessions().subscribe((sessions) => {
      this.sessions.set(sessions);
    });
  }

  revokeSession(sessionId: string) {
    return this.#profileApiService
      .revokeSession(sessionId)
      .pipe(tap(() => this.loadSessions()));
  }

  revokeAllSessions() {
    return this.#profileApiService.revokeAllSessions();
  }

  loadGeminiKey(): void {
    this.#authApiService.getGeminiKey().subscribe((response) => {
      this.geminiApiKey.set(response.geminiApiKey);
    });
  }

  saveGeminiKey(): void {
    const key = this.geminiApiKey()?.trim() || null;
    this.#authApiService.updateGeminiKey(key).subscribe();
  }

  changeLanguage(): void {
    const language = this.language()?.trim();
    this.#authApiService.updateLanguage(language).subscribe();
  }

  saveLlmConfig(): Observable<ILlmConfigDto> {
    const config = {
      llmAuthToken: this.llmAuthToken()?.trim() || null,
      llmModelName: this.llmModelName()?.trim() || null,
      llmUrl: this.llmUrl()?.trim() || null,
    };
    return this.#authApiService.updateLlmConfig(config);
  }

  saveUseOwnLlm(): Observable<IUseOwnLlmDto> {
    return this.#authApiService.updateUseOwnLlm(this.useOwnLlm());
  }

  loadLlmModels(): void {
    this.isLoadingLlmModels.set(true);
    this.llmModelsError.set(null);
    this.llmModels.set([]);

    this.#authApiService.getLlmModels().subscribe({
      complete: () => {
        this.isLoadingLlmModels.set(false);
      },
      error: (err: HttpErrorResponse) => {
        const apiError = err.error as { message?: string };
        this.llmModelsError.set(
          apiError?.message || 'Failed to load LLM models.',
        );
      },
      next: (response) => {
        this.llmModels.set(response.data);
        const currentModelName = this.llmModelName();
        const modelExists = response.data.some(
          (model) => model.id === currentModelName,
        );
        if (!modelExists) {
          this.llmModelName.set(null);
        }
      },
    });
  }
}
